import { ColumnDef } from '@tanstack/react-table'
import type { ObjectVersion } from '@/types/entities'
import { formatBytes } from './utils/formatSize'
import { Button } from '@/components/ui/button'
import { FileJson, MoreHorizontal, Download, Info, Shield, ShieldAlert, ShieldCheck, BarChart3, Microscope, UserCheck, Clock, RefreshCw } from 'lucide-react'
import { AnomalyIcons, type AnomalyData } from '@/components/anomaly/AnomalyIcons'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { useState } from 'react'
import type React from 'react'
import { TranslatableText } from '@/components/ui/TranslatableText'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatDateTime } from '@/lib/dateUtils'

interface VersionActionsProps {
    version: ObjectVersion
    onRetrieveVersion?: (version: ObjectVersion) => void
    onViewRetrievalInfo?: (version: ObjectVersion) => void
    onForensics?: (version: ObjectVersion) => void
    onRunScan?: (version: ObjectVersion) => void
    t: (key: string) => string
}

function VersionActions({ version, onRetrieveVersion, onViewRetrievalInfo, onForensics, onRunScan, t }: VersionActionsProps) {
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    onCloseAutoFocus={(e) => {
                        e.preventDefault()
                    }}
                >
                    {/* Retrieve and Forensics are passed only to a role that may do them. */}
                    {onRetrieveVersion && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onRetrieveVersion(version)
                                setOpen(false)
                            }}
                        >
                            <Download className="mr-2 h-4 w-4" />
                            {t('objects.versions.retrieveVersion')}
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onViewRetrievalInfo?.(version)
                            setOpen(false)
                        }}
                    >
                        <Info className="mr-2 h-4 w-4" />
                        {t('objects.versions.viewRetrievalInfo')}
                    </DropdownMenuItem>
                    {onForensics && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onForensics(version)
                                setOpen(false)
                            }}
                        >
                            <Microscope className="mr-2 h-4 w-4" />
                            {t('objects.versions.forensics')}
                        </DropdownMenuItem>
                    )}
                    {/* Scan it again. Not offered while a scan of it is already queued or running. */}
                    {onRunScan && !version.scanRequest?.open && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onRunScan(version)
                                setOpen(false)
                            }}
                        >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            {t('scanRequests.run')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export function createObjectVersionColumns(
    object: { bootDisk: string | null; fullNo: string | null; intNo: string | null; logNo: string | null },
    t: (key: string) => string,
    onShowJson?: (title: string, content: string | null) => void,
    onRetrieveVersion?: (version: ObjectVersion) => void,
    onViewRetrievalInfo?: (version: ObjectVersion) => void,
    onShowAnalytics?: (analyticsText: string, version: ObjectVersion) => void,
    onShowAnomalyChart?: () => void,
    onForensics?: (version: ObjectVersion) => void,
    // Scan a version again; passed only to a role that may (scan.run).
    onRunScan?: (version: ObjectVersion) => void,
    // The app's language, not the browser's. A column factory cannot call useTranslation, so the
    // page passes i18n.language in alongside t — they are the same setting and always travel
    // together. The default exists only so the signature stays valid after the optional
    // callbacks above it; every real caller passes it.
    locale: string = 'en-GB',
): ColumnDef<ObjectVersion>[] {
    return [
        {
            accessorKey: 'objectPrimary',
            header: t('objects.versions.objectPrimary'),
            size: 80,
            minSize: 70,
            maxSize: 100,
            cell: ({ row }) => <span className="text-sm font-medium">{row.getValue('objectPrimary') ? <TranslatableText text={row.getValue('objectPrimary') as string} /> : '-'}</span>,
        },
        {
            accessorKey: 'objectSecondary',
            header: t('objects.versions.objectSecondary'),
            size: 90,
            minSize: 80,
            maxSize: 110,
            cell: ({ row }) => {
                const objectSecondary = row.getValue('objectSecondary') as string
                const chainSet = row.original.chainSet // 'Primary' | 'Secondary' | null — from Recovery_Chain view
                const analytics = row.original.objectVersionStatus

                if (!objectSecondary) {
                    return <div className="text-sm text-muted-foreground">-</div>
                }

                // Only highlight versions that are in the recovery chain AND are not compromised.
                // chainSet is set by the Recovery_Chain view which determines the correct Primary/Secondary chains.
                // Clean (4) and Information (5) both render green, so both qualify for the chain highlight.
                if (!chainSet || (analytics !== 4 && analytics !== 5)) {
                    return <div className="text-sm">{objectSecondary}</div>
                }

                // Colour by version type within the chain
                // Boot = darkest green, Full = medium green, Int = light green, Log = lightest green
                const type = objectSecondary.toLowerCase()
                let bgStyle: React.CSSProperties = {}
                let textColor = ''

                if (type === 'boot') {
                    bgStyle = { backgroundColor: 'rgb(102, 170, 39)' }
                    textColor = 'text-white'
                } else if (type === 'full') {
                    bgStyle = { backgroundColor: 'rgb(148, 216, 85)' }
                    textColor = 'text-white'
                } else if (type === 'int') {
                    bgStyle = { backgroundColor: 'rgb(186, 230, 145)' }
                    textColor = 'text-green-900'
                } else if (type === 'log') {
                    bgStyle = { backgroundColor: 'rgb(242, 250, 234)' }
                    textColor = 'text-green-800'
                }

                return (
                    <div
                        className={bgStyle.backgroundColor ? `px-1.5 py-0.5 rounded ${textColor} text-xs font-medium` : 'text-sm'}
                        style={bgStyle}
                    >
                        {objectSecondary}
                    </div>
                )
            },
        },
        {
            accessorKey: 'objectVersionStatus',
            header: t('objects.versions.status'),
            size: 90,
            minSize: 70,
            maxSize: 120,
            cell: ({ row }) => {
                const status = row.getValue('objectVersionStatus') as number | null
                const analyticsText = row.original.analyticsText

                if (status == null) return <span className="text-xs text-muted-foreground">-</span>

                // The RAG circle (red/amber/green — the codes the hierarchy status is computed from)
                // PLUS the advisory icon to its right, no text. Click either to open the report.
                // A person's change to the status is marked beside it: approved (the status is now a
                // person's decision, not the scan's) or waiting for a second person (nothing changed yet).
                const changed = row.original.analyticsSource === 'override'
                const waiting = (row.original.statusChanges || []).some((c) => c.state === 'pending')
                const scanning = !!row.original.scanRequest?.open
                const indicator = (
                    <span className="inline-flex items-center gap-1">
                        <AnalyticsStatusIndicator status={status} size="h-4 w-4" showIcon />
                        {changed && <span title={t('objects.statusChange.markerChanged')}><UserCheck className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" aria-label={t('objects.statusChange.markerChanged')} /></span>}
                        {waiting && <span title={t('objects.statusChange.markerPending')}><Clock className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" aria-label={t('objects.statusChange.markerPending')} /></span>}
                        {scanning && <span title={t('scanRequests.marker')}><RefreshCw className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" aria-label={t('scanRequests.marker')} /></span>}
                    </span>
                )

                return analyticsText && analyticsText.trim() !== '' ? (
                    <button
                        type="button"
                        className="inline-flex items-center transition-opacity hover:opacity-80"
                        onClick={(e) => { e.stopPropagation(); onShowAnalytics?.(analyticsText, row.original) }}
                        title={t('objects.versions.viewAnalytics')}
                    >
                        {indicator}
                    </button>
                ) : indicator
            },
        },
        {
            accessorKey: 'vaultDatetime',
            header: t('objects.versions.vaultDatetime'),
            size: 130,
            minSize: 110,
            maxSize: 160,
            cell: ({ row }) => {
                const datetime = row.getValue('vaultDatetime') as string | null
                if (!datetime) return '-'
                return (
                    <span className="whitespace-nowrap text-sm">
                        {formatDateTime(datetime, locale)}
                    </span>
                )
            },
        },
        {
            accessorKey: 'objectSize',
            header: () => <div className="text-right">{t('objects.versions.objectSize')}</div>,
            size: 100,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => {
                const size = row.getValue('objectSize') as number | null
                if (size === null || size === undefined) return <div className="text-right text-muted-foreground">-</div>
                return (
                    <div className="text-right">
                        <span className="text-sm whitespace-nowrap">
                            {formatBytes(size)}
                        </span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionMetadata',
            header: t('objects.versions.metadata'),
            size: 70,
            minSize: 50,
            maxSize: 80,
            cell: ({ row }) => {
                const metadata = row.getValue('versionMetadata') as string | null
                if (!metadata) return <span className="text-muted-foreground">-</span>
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1"
                        onClick={() => onShowJson?.(t('objects.versions.metadata'), metadata)}
                    >
                        <FileJson className="h-3.5 w-3.5" />
                    </Button>
                )
            },
        },
        {
            accessorKey: 'vaultFiles',
            header: t('objects.versions.files'),
            size: 60,
            minSize: 50,
            maxSize: 70,
            cell: ({ row }) => {
                const vaultFiles = row.getValue('vaultFiles') as string | null
                if (!vaultFiles) return <span className="text-muted-foreground">-</span>
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1"
                        onClick={() => onShowJson?.(t('objects.versions.files'), vaultFiles)}
                    >
                        <FileJson className="h-3.5 w-3.5" />
                    </Button>
                )
            },
        },
        {
            accessorKey: 'vaultName',
            header: t('objects.versions.vaultID'),
            size: 80,
            minSize: 70,
            maxSize: 100,
            cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.getValue('vaultName') ? <TranslatableText text={row.getValue('vaultName') as string} /> : '-'}</span>,
        },
        {
            accessorKey: 'vaultLocation',
            header: t('objects.versions.vaultLocation'),
            size: 90,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.getValue('vaultLocation') ? <TranslatableText text={row.getValue('vaultLocation') as string} /> : '-'}</span>,
        },
        {
            id: 'anomalyData',
            header: t('objects.versions.anomaly'),
            size: 100,
            minSize: 80,
            maxSize: 140,
            cell: ({ row }) => {
                // Parse anomaly_data — supports new format {timing: 1, files: 2}
                // and legacy format {anomalies: [{type: "fileMismatch", ...}]}
                let anomalyData: AnomalyData = null
                const raw = row.original.anomalyData
                if (raw && typeof raw === 'object') {
                    if ('timing' in raw || 'size' in raw || 'files' in raw || 'shape' in raw) {
                        // New format — direct category severities
                        anomalyData = raw as AnomalyData
                    } else if ('anomalies' in raw && Array.isArray((raw as any).anomalies)) {
                        // Legacy format — convert anomaly types to categories
                        const cats: Record<string, number> = {}
                        for (const a of (raw as any).anomalies) {
                            const type = a.type as string
                            let cat = 'shape'
                            if (type === 'timing' || type === 'cadence') cat = 'timing'
                            else if (type === 'size' || type === 'sizeDelta') cat = 'size'
                            else if (type === 'fileCount' || type === 'fileMismatch') cat = 'files'
                            cats[cat] = Math.min((cats[cat] || 0) + 1, 2) as 1 | 2
                        }
                        if (Object.keys(cats).length > 0) {
                            anomalyData = cats as AnomalyData
                        }
                    }
                } else if (row.original.anomalyReasons) {
                    // Fallback: use anomalyReasons array
                    let reasons: string[] = []
                    try {
                        if (typeof row.original.anomalyReasons === 'string') reasons = JSON.parse(row.original.anomalyReasons)
                        else if (Array.isArray(row.original.anomalyReasons)) reasons = row.original.anomalyReasons
                    } catch { /* ignore */ }
                    if (reasons.length > 0) {
                        const cats: Record<string, number> = {}
                        for (const r of reasons) cats[r] = 1
                        anomalyData = cats as AnomalyData
                    }
                }

                const score = row.original.anomalyScore
                if (score === 0 || (score == null && row.original.anomaly != null && Number(row.original.anomaly) === 0)) {
                    anomalyData = null // clean — will show green check
                }

                return (
                    <div className="flex items-center gap-1.5">
                        <AnomalyIcons anomalyData={anomalyData} />
                        {row.original.anomalyReasons && (row.original.anomalyReasons as any[]).includes?.('size') && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onShowAnomalyChart?.()
                                }}
                                title="View size anomaly chart"
                            >
                                <BarChart3 className="h-3 w-3 text-muted-foreground" />
                            </Button>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            header: t('common.actions'),
            size: 80,
            minSize: 70,
            maxSize: 100,
            cell: ({ row }) => {
                const version = row.original
                return <VersionActions
                    version={version}
                    onRetrieveVersion={onRetrieveVersion}
                    onViewRetrievalInfo={onViewRetrievalInfo}
                    onForensics={onForensics}
                    onRunScan={onRunScan}
                    t={t}
                />
            },
        },
    ]
}
